<script lang="ts">
import { Tooltip } from 'bootstrap'
import consumer from 'channels/consumer'
import logoPlaceholder from 'frontend/_assets/trophy_icon.png'
import { RouteName } from 'frontend/_setup/setupRouter/types/RouteName'
import { getEventOrganizationUser, isAdmin } from 'frontend/common/access-helpers'
import { useCurrentUser } from 'frontend/common/composables/useCurrentUser'
import { useDateFormat } from 'frontend/common/composables/useDateFormat'
import Breadcrumbs from 'frontend/common/layout/breadcrumbs.vue'
import HeaderUser from 'frontend/common/layout/components/header-user.vue'
import { useSignOut } from 'frontend/common/sign-out'
import { useEventAccess } from 'frontend/common/use-event-access'
import { DataModelActionType } from 'frontend/roles/enum/DataModelActionType'
import { useTheme } from 'frontend/themes/composables/useTheme'
import ToggleComponent from 'frontend/ui/components/ToggleComponent.vue'
import { UsersService } from 'frontend/users/users-service'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { mapGetters } from 'vuex'

import ConnectionPreview from './connection-preview.vue'
import LayoutClock from './layout-clock.vue'
import LayoutSpotlightSearch from './layout-spotlight-search.vue'
import NotSentCcmIndicator from './not-sent-ccm-indicator.vue'
import SystemNotificationsIndicator from './system-notifications/system-notifications-indicator.vue'

const ARE_BREADCRUMBS_ENABLED = true

export default {
  name: 'MainHeader',
  emits: ['toggleMenu'],
  data() {
    return {
      isMenuToggled: false,
      events: [],
      timeoutToClear: null,
      logoPlaceholder,
      mobileControlsOpen: null,
    }
  },
  setup() {
    const blocker = ref(false)
    const isCableConnected = ref(!consumer.connection.disconnected)
    const windowWidth = ref(window.innerWidth)
    const accessData = useEventAccess()
    const route = useRoute()
    const router = useRouter()
    const { currentUser } = useCurrentUser()
    const { isDarkModeEnabled } = useTheme()
    const { getDateFormatString } = useDateFormat()

    return {
      getDateFormatString,
      service: new UsersService(),
      route,
      router,
      ...useSignOut(blocker),
      isCableConnected,
      windowWidth,
      ...accessData,
      isDarkModeEnabled,
      currentUser,
    }
  },
  computed: {
    breadcrumbItems() {
      const items = []

      if (ARE_BREADCRUMBS_ENABLED) {
        items.push({
          text: this.currentOrganization?.customAppName || 'TMM',
          to: { name: RouteName.Events },
          isLogo: true,
        })
      }

      if (this.currentEvent && ARE_BREADCRUMBS_ENABLED) {
        items.push({
          text: this.currentEvent.name,
          to: { name: RouteName.EventDashboard },
          tooltipOptions: {
            title: this.eventTooltip,
            placement: 'bottom',
            html: true,
          },
        })
      }
      const matched = this.route.matched
      const uniqueNames = []

      for (const match of matched) {
        if (
          (match.meta?.title || match.meta?.breadcrumbText) &&
          match.meta?.isBreadcrumbItem !== false &&
          ARE_BREADCRUMBS_ENABLED
        ) {
          let toName = null
          if (match.name) {
            toName = match.name
          }
          if (!toName && match.children?.[0]?.name) {
            toName = match.children[0].name
          }
          if (toName && !uniqueNames.includes(toName)) {
            let text =
              typeof match.meta.title === 'function' ? match.meta.title() : match.meta.title
            if (match.meta.breadcrumbText) {
              if (typeof match.meta.breadcrumbText === 'function') {
                text = match.meta.breadcrumbText(this)
              } else {
                text = match.meta.breadcrumbText
              }
            }
            items.push({
              text,
              to: { name: toName },
            })
            uniqueNames.push(toName)
          }
        }
      }
      if (items.length) {
        items[items.length - 1].isCurrent = true
      }
      return items
    },
    eventTooltip() {
      let result = this.currentEvent.name || 'Current event'
      if (this.formattedStartDate && this.formattedEndDate) {
        result += `<br>${this.formattedStartDate} - ${this.formattedEndDate}`
      }
      return result
    },
    formattedStartDate() {
      if (this.currentEvent.startDate) {
        return dayjs(this.currentEvent.startDate).format(
          this.getDateFormatString('dateWithShortMonth'),
        )
      }
      return null
    },
    formattedEndDate() {
      if (this.currentEvent.endDate) {
        return dayjs(this.currentEvent.endDate).format(
          this.getDateFormatString('dateWithShortMonth'),
        )
      }
      return null
    },
    eou() {
      if (this.currentEvent) {
        return getEventOrganizationUser(this.currentUser, this.currentEvent.slug)
      } else {
        return null
      }
    },
    ccmAvailable() {
      if (!this.currentOrganization) return false
      if (!this.currentEvent) return false
      if (!this.eou || !this.can(DataModelActionType.Read, 'ClientCommunicationMessage'))
        return false
      if (this.$route.path.includes('passenger-portal')) return false
      console.debug('CCMAvailable!')
      return true
    },
    notificationsAvailable() {
      if (!this.currentOrganization) return false
      if (!this.currentEvent) return false
      if (!this.eou || !this.can(DataModelActionType.Read, 'SystemNotification')) return false
      if (this.$route.path.includes('passenger-portal')) return false
      console.debug('NotificationsAvailable!')
      return true
    },
    ...mapGetters('currentContext', ['currentEvent', 'currentCluster']),
    isAdmin() {
      return isAdmin(this.eou)
    },
    isImpersonatingOtherUser() {
      return !!this.currentUser?.impersonatedUserId
    },

    currentOrganization() {
      if (!this.currentUser?.currentOrganizationId) {
        return {}
      } else {
        return (this.currentUser?.organizations || []).find(
          o => o.id == this.currentUser.currentOrganizationId,
        )
      }
    },
    eventLogo() {
      return this.currentEvent?.logoUrls?.max40x40
        ? `/api${this.currentEvent?.logoUrls?.max40x40}`
        : this.logoPlaceholder
    },
    isSmallDevice() {
      return this.windowWidth <= 576 // sm bootstrap $grid-breakpoint
    },
  },
  mounted() {
    this.$eventHub.$on('cable-connection-check', this.updateCableConnectionStatus)
    this.$eventHub.$on('windowResize', this.setWindowWidth)
  },
  unmounted() {
    clearTimeout(this.timeoutToClear)
    this.$eventHub.$off('cable-connection-check', this.updateCableConnectionStatus)
    this.$eventHub.$off('windowResize', this.setWindowWidth)
  },
  methods: {
    setWindowWidth() {
      this.windowWidth = window.innerWidth
    },
    updateCableConnectionStatus() {
      clearTimeout(this.timeoutToClear)
      this.isCableConnected = !consumer.connection.disconnected
      this.$nextTick(() => {
        const tooltip = Tooltip.getInstance(this.$refs.cableConnectionEl)
        if (tooltip?.show) {
          tooltip.show()
        }
        if (this.isCableConnected) {
          this.timeoutToClear = setTimeout(() => {
            if (tooltip?.hide) {
              tooltip.hide()
            }
          }, 3000)
        }
      })
    },
    toggleMobileControls() {
      this.mobileControlsOpen = !this.mobileControlsOpen
    },
  },
  components: {
    HeaderUser,
    ToggleComponent,
    Breadcrumbs,
    ConnectionPreview,
    NotSentCcmIndicator,
    LayoutClock,
    LayoutSpotlightSearch,
    SystemNotificationsIndicator,
  },
}
</script>

<template lang="pug">
header.header.d-flex.align-items-center.justify-content-between
  .left-section.d-flex.align-items-center
    breadcrumbs(:breadcrumbItems="breadcrumbItems")

  .right-section.d-flex.align-items-center
    .event-info-wrapper
      i.bi-arrow-right.pe-2.ps-1(v-if="!!currentCluster")
      span(v-if="!!currentCluster" v-tooltip="'Current cluster'") {{ currentCluster.name }}

    .controls-wrapper
      .controls(
        :class="{ 'mobile-controls-open': mobileControlsOpen, 'mobile-controls-closed': mobileControlsOpen === false, 'is-mobile-view': isSmallDevice }"
      )
        .cable-connection-status-wrapper(
          v-if="currentOrganization"
          :class="{ connected: isCableConnected }"
        )
          .cable-connection-status(
            ref="cableConnectionEl"
            :class="{ connected: isCableConnected }"
          )
          .text {{ isCableConnected ? 'The app is online' : 'The app is offline' }}
        layout-spotlight-search(v-if="currentUser && $route?.params?.eventSlug?.length")
        toggle-component(
          v-model:isChecked="isDarkModeEnabled"
          checked-icon="mi mi-dark-mode"
          unchecked-icon="mi mi-light-mode"
        )
        .d-flex.align-items-center
          .indicator-buttons
            connection-preview(v-if="currentEvent")
            not-sent-ccm-indicator(v-if="ccmAvailable")
            //- chat-indicator(v-if="!!currentUser && $route?.params?.eventSlug?.length")
            system-notifications-indicator(v-if="notificationsAvailable")
          layout-clock
          button.btn-close.btn-close-controls(@click="toggleMobileControls()" type="button")

        button.btn.text-primary.ps-3.pe-3.py-2.px-2.border-0(
          v-if="isSmallDevice"
          @click="toggleMobileControls()"
        )
          i(:class="mobileControlsOpen ? 'bi-x-diamond-fill' : 'bi-x-diamond'")
        header-user
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;
@import '../../../frontend_stylesheets/_variables.scss';

.header {
  padding: 0 16px;
  border-bottom: 1px solid #b8c1cc;

  .erebus-app[data-theme='dark'] & {
    border-color: #3b434d;
  }

  background: map.get(themeVariables.$colors, surfaceBright);
}

.left-section {
  gap: 24px;
}

.right-section {
  gap: 24px;
}

.controls-wrapper {
  gap: 0px;
}

.controls {
  gap: 12px;
}

.indicator-buttons {
  display: flex;
  gap: 8px;
}

.controls-wrapper {
  display: flex;
  align-items: center;
  padding-left: 0.5rem;
  @include for-size(small-devices) {
    padding-left: 0px;
  }

  .controls {
    display: flex;
    align-items: center;

    .btn-close-controls {
      display: none;
    }

    @include for-size(small-devices) {
      &.is-mobile-view {
        position: fixed;
        z-index: 1;
        top: -60px;
        left: calc($condensed-sidebar-width + 0.5rem);
        background: white;
        width: calc(100% - $condensed-sidebar-width - 1rem);
        padding: 10px 10px 10px 20px;
        box-shadow: 0 3px 5px rgba(0, 0, 0, 0.15);
        border-radius: $btn-border-radius-xs;
        justify-content: space-between;
        height: $mobile-controls-height;
      }

      &.mobile-controls-open {
        animation: slide-down 0.5s forwards;
        -webkit-animation: slide-down 0.5s forwards;
      }

      &.mobile-controls-closed {
        animation: slide-up 0.5s forwards;
        -webkit-animation: slide-up 0.5s forwards;
      }

      @keyframes slide-down {
        0% {
          transform: translateY(0%);
        }
        100% {
          transform: translateY(
            calc($header-height + $mobile-controls-height + $mobile-controls-top-gap)
          );
        }
      }

      @keyframes slide-up {
        0% {
          transform: translateY(
            calc($header-height + $mobile-controls-height + $mobile-controls-top-gap)
          );
        }
        100% {
          transform: translateY(0%);
        }
      }

      .btn-close-controls {
        display: block;
      }
    }
  }
}

.cable-connection-status-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #cc6d00;

  &.connected {
    color: #00be00;
  }
}
@keyframes blink {
  0% {
    background-color: var(--custom-primary);
    color: white;
  }
  50% {
    background-color: orange;
    color: black;
  }
  100% {
    background-color: var(--custom-primary);
    color: white;
  }
}
.blink {
  animation: blink 2s ease-in-out infinite;
}
</style>
